import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@components/Button';
import { ModalConfirm } from '@components/Modal';
import BrandManageModal from '@features/BrandManageModal';
import PageHeader from '@features/PageHeader';
import { accountActions, alertActions, authActions, brandsActions } from '@actions';
import { brandsService } from '@services';
import { useTranslation } from '@hooks';
import { handleApiError } from '@helpers/api';
import { FORM_TYPE } from '@constants/brands';
import { DEFAULT_PAGE_SIZE } from '@constants/pagination';
import { BrandsIcon } from 'assets/images/icons';
import BrandsTable from './BrandsTable';

const Brands = () => {
    const [selectedBrand, setSelectedBrand] = useState({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [modal, setModal] = useState({ open: false, type: FORM_TYPE.NEW });

    const locations = useSelector(state => state.account?.account?.companies || []);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    useEffect(() => {
        dispatch(brandsActions.get(page, rowsPerPage));
    }, [page, rowsPerPage, dispatch]);

    useEffect(() => {
        // INFO: get all brands on unmount for using in other components
        return () => {
            dispatch(brandsActions.getAll());
        };
    }, []);

    const refetchFirstPage = async () => {
        if (page === 0) {
            await dispatch(brandsActions.get(page, rowsPerPage));
        } else {
            setPage(0); // INFO:Triggers refetch of templates
        }
    };

    const REFETCH_MAP = {
        [FORM_TYPE.EDIT]: async () => await dispatch(brandsActions.get(page, rowsPerPage)),
        [FORM_TYPE.NEW]: () => refetchFirstPage(),
    };

    const onCloseBrandModal = () => {
        setSelectedBrand(null);
        setModal({ open: false, type: FORM_TYPE.NEW });
    };

    const onDelete = async () => {
        try {
            await brandsService.deleteBrand(selectedBrand?.id);
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
            dispatch(accountActions.get());
            dispatch(authActions.getCurrentUser());
            if (page === 0) {
                await dispatch(brandsActions.get(page, rowsPerPage));
            } else {
                setPage(0);
            }
            setDeleteModalOpen(false);
            setSelectedBrand({});
        } catch (error) {
            handleApiError(error, 'alertMessages.deleteFail', { amount: error.count });
        }
    };

    return (
        <div style={{ paddingBottom: '16px' }}>
            <PageHeader
                styles={{ marginBottom: '16px' }}
                icon={<BrandsIcon />}
                title={t('SettingsBrands.title')}
                subtitle={t('SettingsBrands.subtitle')}
            >
                <Button variant="contained" onClick={() => setModal(prev => ({ ...prev, open: true }))}>
                    {t('buttons.createBrand')}
                </Button>
            </PageHeader>
            <BrandsTable
                locations={locations}
                setSelectedBrand={setSelectedBrand}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                setModal={setModal}
                setDeleteModalOpen={setDeleteModalOpen}
            />
            {modal.open ? (
                <BrandManageModal
                    selectedBrand={selectedBrand}
                    modal={modal}
                    onClose={onCloseBrandModal}
                    refetchCallback={REFETCH_MAP[modal.type]}
                />
            ) : null}
            {isDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => {
                        setDeleteModalOpen(false);
                        setSelectedBrand({});
                    }}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDelete}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
        </div>
    );
};

export default Brands;
